import ChoiceTree, { selectId, selectLabel } from '@alkem/react-ui-choicetree';
import { remove as removeDiacritics } from 'diacritics';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import './multiple-selector.scss';

export default class ReactUiMultipleImmutableSelector extends PureComponent {
  static defaultProps = {
    selection: [],
    emptyLabel: '',
    scrollable: false,
    noToggleIcon: false,
    placeholder: '',
    removeFiltered: false,
    selectors: {
      selectId,
      selectLabel,
    },
  };

  constructor(props) {
    super(props);
    this.onInputChange = this.onInputChange.bind(this);
    this.clearFilter = this.clearFilter.bind(this);
    this.onFilterOptions = debounce(this.onFilterOptions.bind(this), 500);
    this.state = {
      filterValue: '',
    };
  }

  onInputChange(event) {
    this.setState({ filterValue: event.target.value });
    this.onFilterOptions();
  }

  onFilterOptions() {
    // debounced function
    if (this.state.filterValue) {
      this.setState({
        filteredOptions: this.filterOptions(this.props.options),
      });
    } else {
      this.clearFilter();
    }
  }

  clearFilter() {
    this.setState({ filterValue: '', filteredOptions: null });
  }

  filterOptions(options) {
    const { removeFiltered } = this.props;

    const nonMatchingOptionIds = [];
    let newOptions = options.map((option) => {
      let haveMatchingChild = false;
      let children = option.get('children');
      if (children.size) {
        children = this.filterOptions(children);
        haveMatchingChild = children.some((child) => !child.get('hide'));
      }

      const match = haveMatchingChild || this.matchFilters(option);
      if (!match) {
        nonMatchingOptionIds.push(option.get('id'));
      }

      return option.withMutations((opt) =>
        opt.set('children', children).set('hide', !match)
      );
    });

    if (nonMatchingOptionIds.length && removeFiltered) {
      newOptions = newOptions.filter(
        (option) => nonMatchingOptionIds.indexOf(option.get('id')) === -1
      );
    }
    return newOptions;
  }

  matchFilters(option) {
    const { selectors } = this.props;
    const { filterValue } = this.state;
    const filter = removeDiacritics(filterValue.trim().toLowerCase());
    const optionLabel = selectors.selectLabel(option);
    const label =
      optionLabel && removeDiacritics(optionLabel.trim().toLowerCase());
    return label.indexOf(filter) >= 0;
  }

  isEmpty(options) {
    return !options.size || options.every((opt) => opt.get('hide'));
  }

  render() {
    const {
      id,
      options,
      selection,
      emptyLabel,
      scrollable,
      noToggleIcon,
      placeholder,
      onSelect,
      selectors,
    } = this.props;
    const { filterValue, filteredOptions } = this.state;
    const treeOptions = filteredOptions || options;

    const isEmpty = this.isEmpty(treeOptions);
    return (
      <div id={id} className="ReactUiMultipleSelector" onClick={this.onClick}>
        <div className="ReactUiMultipleSelector__inputWrapper">
          <div className="ReactUiMultipleSelector__searchIconWrapper">
            <div className="ReactUiMultipleSelector__hiddenItemsWrapper">
              <span className="ChoiceTreeItem__chevron mdi mdi-chevron-right" />
              <i className="ReactUiCheckbox__checkbox" />
            </div>
            <i className="ReactUiMultipleSelector__searchIcon mdi mdi-24px mdi-magnify" />
          </div>
          <input
            className="ReactUiMultipleSelector__inputText"
            type="text"
            value={filterValue}
            onChange={this.onInputChange}
            placeholder={placeholder}
          />
          {filterValue && (
            <i
              className="ReactUiMultipleSelector__clearIcon mdi mdi-24px mdi-close"
              onClick={this.clearFilter}
            />
          )}
        </div>
        {!isEmpty && (
          <ChoiceTree
            id={`${id}-choicetree`}
            options={treeOptions}
            onSelect={onSelect}
            selectedPath={selection}
            noToggleIcon={noToggleIcon}
            scrollable={scrollable}
            selectors={selectors}
            multiple
            isStatic
          />
        )}
        {isEmpty && (
          <div className="ReactUiMultipleSelector__emptyLabel">
            {emptyLabel}
          </div>
        )}
      </div>
    );
  }
}

ReactUiMultipleImmutableSelector.propTypes = {
  id: PropTypes.string.isRequired,
  options: ImmutablePropTypes.list.isRequired,
  selection: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  emptyLabel: PropTypes.string,
  scrollable: PropTypes.bool,
  noToggleIcon: PropTypes.bool,
  placeholder: PropTypes.string,
  removeFiltered: PropTypes.bool,
  selectors: PropTypes.shape({
    selectId: PropTypes.func.isRequired,
    selectLabel: PropTypes.func.isRequired,
  }).isRequired,
};
